import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import { supabase } from "@/lib/supabase";
import { useResetPassword } from "@/pages/auth/hooks/useResetPassword";

function FieldMessage({ tone, text }: { tone: "error" | "info"; text: string }) {
  const wrap =
    tone === "error"
      ? "border-accent-200 bg-accent-50 text-accent-800"
      : "border-primary-200 bg-primary-50 text-primary-800";
  const icon = tone === "error" ? "ri-error-warning-line" : "ri-mail-check-line";
  return (
    <div className={`flex items-start gap-2 rounded-md border px-4 py-3 ${wrap}`}>
      <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
        <i className={icon} />
      </span>
      <p className="text-xs leading-relaxed">{text}</p>
    </div>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { sending, saving, error: hookError, requestReset, updatePassword, clearError } = useResetPassword();
  const [recovered, setRecovered] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const hash = (window.location.hash || "").toLowerCase();
    if (hash.includes("type=recovery")) setRecovered(true);
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setRecovered(true);
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const errorText = localError ?? hookError;

  const handleRequest = async () => {
    clearError();
    setLocalError(null);
    if (!email.trim()) {
      setLocalError("请输入注册时使用的邮箱");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setLocalError("邮箱格式不正确，请检查后重试");
      return;
    }
    const ok = await requestReset(email);
    if (ok) setRequestSent(true);
  };

  const handleUpdate = async () => {
    clearError();
    setLocalError(null);
    if (password.length < 6) {
      setLocalError("新密码至少需要 6 位");
      return;
    }
    if (password !== confirm) {
      setLocalError("两次输入的密码不一致");
      return;
    }
    const ok = await updatePassword(password);
    if (ok) navigate("/licenses", { replace: true });
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex flex-1 items-center justify-center px-4 py-16 md:py-24">
        <div className="lh-fade-up w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className={recovered ? "ri-lock-password-line text-2xl" : "ri-mail-lock-line text-2xl"} />
          </span>

          {recovered ? (
            <>
              <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">设置新密码</h1>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                请为你的账号设置一个新的登录密码，设置完成后即可使用新密码登录。
              </p>
              <div className="mt-6 flex flex-col gap-4">
                <div>
                  <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    新密码 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setLocalError(null);
                    }}
                    className={inputClass}
                    placeholder="至少 6 位字符"
                  />
                </div>
                <div>
                  <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    确认新密码 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(event) => {
                      setConfirm(event.target.value);
                      setLocalError(null);
                    }}
                    className={inputClass}
                    placeholder="再次输入新密码"
                  />
                </div>
                {errorText && <FieldMessage tone="error" text={errorText} />}
                <button
                  type="button"
                  onClick={() => void handleUpdate()}
                  disabled={saving}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-loader-4-line animate-spin" />
                      </span>
                      保存中…
                    </>
                  ) : (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-check-line" />
                      </span>
                      确认修改
                    </>
                  )}
                </button>
              </div>
            </>
          ) : requestSent ? (
            <>
              <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">重置邮件已发送</h1>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                我们已向 <span className="font-medium text-foreground-950">{email.trim()}</span> 发送了一封密码重置邮件。
                请打开邮件并点击其中的链接，即可设置新密码。
              </p>
              <div className="mt-5">
                <FieldMessage tone="info" text="没收到邮件？请检查垃圾邮件文件夹，或稍等 1-2 分钟后重新发送。" />
              </div>
              <button
                type="button"
                onClick={() => {
                  setRequestSent(false);
                  clearError();
                }}
                className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-refresh-line" />
                </span>
                重新发送
              </button>
            </>
          ) : (
            <>
              <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">找回密码</h1>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                输入注册时使用的邮箱，我们会向你发送一封密码重置邮件。
              </p>
              <div className="mt-6 flex flex-col gap-4">
                <div>
                  <label htmlFor="reset-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    注册邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setLocalError(null);
                    }}
                    className={inputClass}
                    placeholder="you@example.com"
                  />
                </div>
                {errorText && <FieldMessage tone="error" text={errorText} />}
                <button
                  type="button"
                  onClick={() => void handleRequest()}
                  disabled={sending}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-loader-4-line animate-spin" />
                      </span>
                      发送中…
                    </>
                  ) : (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-send-plane-line" />
                      </span>
                      发送重置邮件
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          <div className="mt-6 border-t border-background-200 pt-4 text-center">
            <Link
              to="/auth"
              className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-950"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              返回登录 / 注册
            </Link>
          </div>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}